import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  SpaceBetween,
  Button,
  FormField,
  Input,
  Box,
  Header,
  Toggle,
  ColumnLayout,
  Select,
  SelectProps,
} from '@cloudscape-design/components';
import { QuestionFormParams } from './QuestionFormWizard';
import { FileData } from '../textEditor/TextEditor';
import TextEditor from '../textEditor/TextEditor';
import { SubQuestion, ResponseType, ShowOn } from 'src/types/helios/Question';
import { MARKDOWN_TEXT_WARNING } from 'src/contstant/commonLabels';
import RequiredFieldLabel from '../RequiredFieldLabel';
import { FileMetadata } from '../FileDisplay';
import { getSubQuestionFieldKey } from 'src/helper/questionHelper';
import { useFeatureFlags } from 'src/context/FeatureFlagsContext';

interface SubQuestionSidePanelProps {
  mode: 'add' | 'edit';
  editingIndex?: number;
  formData: QuestionFormParams;
  setFormData: React.Dispatch<React.SetStateAction<QuestionFormParams>>;
  fileList: Record<string, FileData[]>;
  setFileList: React.Dispatch<React.SetStateAction<Record<string, FileData[]>>>;
  documentFiles?: Record<string, File[]>;
  setDocumentFiles?: React.Dispatch<React.SetStateAction<Record<string, File[]>>>;
  documentFilesMetadata?: Record<string, FileMetadata[]>;
  setDocumentFilesMetadata?: React.Dispatch<
    React.SetStateAction<Record<string, FileMetadata[]>>
  >;
  onClose: () => void;
}

const SubQuestionSidePanel: React.FC<SubQuestionSidePanelProps> = ({
  mode,
  editingIndex,
  formData,
  setFormData,
  fileList,
  setFileList,
  documentFiles,
  setDocumentFiles,
  documentFilesMetadata,
  setDocumentFilesMetadata,
  onClose,
}) => {
  // Feature flags
  const { enableEnhancedResponseTypes } = useFeatureFlags();

  // ERT helper: whether a response type requires options
  const isSelectType = (type: ResponseType): boolean =>
    type === ResponseType.SINGLE_SELECT ||
    type === ResponseType.MULTI_SELECT ||
    type === ResponseType.YES_NO;

  // ERT helper: whether a response type is data-collection (non-scoring)
  const isDataCollectionType = (type: ResponseType): boolean =>
    type !== ResponseType.PASS_FAIL;

  // ERT: Response type dropdown options
  const allResponseTypeOptions: SelectProps.Option[] = [
    { label: 'Pass / Fail', value: ResponseType.PASS_FAIL },
    { label: 'Text Input', value: ResponseType.TEXT },
    { label: 'Numeric Input', value: ResponseType.NUMERIC },
    { label: 'Single Select', value: ResponseType.SINGLE_SELECT },
    { label: 'Multi Select', value: ResponseType.MULTI_SELECT },
    { label: 'Yes / No', value: ResponseType.YES_NO },
    { label: 'Employee Login', value: ResponseType.USER_ALIAS },
  ];

  // When conditional is ON, only data-collection types are allowed
  const responseTypeOptions = formData.hasConditionalSubQuestions
    ? allResponseTypeOptions.filter((opt) => opt.value !== ResponseType.PASS_FAIL)
    : allResponseTypeOptions;

  // ERT: Show on dropdown options
  const showOnOptions: SelectProps.Option[] = [
    { label: 'Pass', value: ShowOn.PASS },
    { label: 'Fail', value: ShowOn.FAIL },
    { label: 'Both (Pass or Fail)', value: ShowOn.BOTH },
  ];

  // Form state for side panel
  const [panelFormData, setPanelFormData] = useState<{
    title: string;
    instruction: string;
    taskName: string;
    taskDescription: string;
    taskDurationDays: number;
    hasTask: boolean;
    requireCommentOnFail: boolean;
    requireEvidenceOnFail: boolean;
    requireCommentOnPass: boolean;
    requireEvidenceOnPass: boolean;
    responseType: ResponseType;
    showWhenParentIs: ShowOn;
    requireComment: boolean;
    requireAttachment: boolean;
    options: string[];
  }>({
    title: '',
    instruction: '',
    taskName: '',
    taskDescription: '',
    taskDurationDays: 1,
    hasTask: false,
    requireCommentOnFail: false,
    requireEvidenceOnFail: false,
    requireCommentOnPass: false,
    requireEvidenceOnPass: false,
    responseType: ResponseType.PASS_FAIL,
    showWhenParentIs: ShowOn.BOTH,
    requireComment: false,
    requireAttachment: false,
    options: [],
  });

  const [subQuestionFiles, setSubQuestionFiles] = useState<Record<string, FileData[]>>(
    {},
  );

  // Separate document states for instruction and taskDescription
  const [instructionDocumentFiles, setInstructionDocumentFiles] = useState<File[]>([]);
  const [instructionDocumentMetadata, setInstructionDocumentMetadata] = useState<
    FileMetadata[]
  >([]);
  const [taskDescriptionDocumentFiles, setTaskDescriptionDocumentFiles] = useState<
    File[]
  >([]);
  const [taskDescriptionDocumentMetadata, setTaskDescriptionDocumentMetadata] = useState<
    FileMetadata[]
  >([]);

  // Track if we've initialized to prevent re-initialization
  const initializedRef = useRef<string | null>(null);

  // Memoize metadata arrays to prevent infinite loops in TextEditor
  const memoizedInstructionMetadata = useMemo(
    () => instructionDocumentMetadata,
    [instructionDocumentMetadata],
  );
  const memoizedTaskDescriptionMetadata = useMemo(
    () => taskDescriptionDocumentMetadata,
    [taskDescriptionDocumentMetadata],
  );

  // Initialize form data when component mounts or editing index changes
  useEffect(() => {
    const initKey = `${mode}_${editingIndex}`;

    // Only initialize once per mode/editingIndex combination
    if (initializedRef.current === initKey) {
      return;
    }

    initializedRef.current = initKey;

    if (
      mode === 'edit' &&
      editingIndex !== undefined &&
      formData.subQuestions &&
      formData.subQuestions[editingIndex]
    ) {
      const subQuestion = formData.subQuestions[editingIndex];
      setPanelFormData({
        title: subQuestion.title || '',
        instruction: subQuestion.instruction || '',
        taskName: subQuestion.taskDetails?.taskName || '',
        taskDescription: subQuestion.taskDetails?.taskDescription || '',
        taskDurationDays: subQuestion.taskDetails?.taskDurationDays || 1,
        hasTask: !!subQuestion.taskDetails,
        requireCommentOnFail: subQuestion.requireCommentOnFail ?? false,
        requireEvidenceOnFail: subQuestion.requireEvidenceOnFail ?? false,
        requireCommentOnPass: subQuestion.requireCommentOnPass ?? false,
        requireEvidenceOnPass: subQuestion.requireEvidenceOnPass ?? false,
        responseType:
          (subQuestion.responseType as ResponseType) ?? ResponseType.PASS_FAIL,
        showWhenParentIs: (subQuestion.showWhenParentIs as ShowOn) ?? ShowOn.BOTH,
        requireComment: subQuestion.requireComment ?? false,
        requireAttachment: subQuestion.requireAttachment ?? false,
        options: subQuestion.options ?? [],
      });

      // Load existing documents for instruction field
      const instructionFieldKey = getSubQuestionFieldKey(editingIndex, 'instruction');
      const instructionMetadata = documentFilesMetadata?.[instructionFieldKey] || [];
      const instructionFiles = documentFiles?.[instructionFieldKey] || [];

      // If we have metadata but no files, create placeholder File objects
      if (instructionMetadata.length > 0 && instructionFiles.length === 0) {
        const placeholderFiles = instructionMetadata.map((metadata) => {
          // Create a placeholder File object for existing documents
          return new File([], metadata.fileName, { type: 'application/octet-stream' });
        });
        setInstructionDocumentFiles(placeholderFiles);
      } else {
        setInstructionDocumentFiles(instructionFiles);
      }
      setInstructionDocumentMetadata(instructionMetadata);

      // Load existing documents for taskDescription field
      const taskDescriptionFieldKey = getSubQuestionFieldKey(
        editingIndex,
        'taskDescription',
      );
      const taskDescriptionMetadata =
        documentFilesMetadata?.[taskDescriptionFieldKey] || [];
      const taskDescriptionFiles = documentFiles?.[taskDescriptionFieldKey] || [];

      // If we have metadata but no files, create placeholder File objects
      if (taskDescriptionMetadata.length > 0 && taskDescriptionFiles.length === 0) {
        const placeholderFiles = taskDescriptionMetadata.map((metadata) => {
          // Create a placeholder File object for existing documents
          return new File([], metadata.fileName, { type: 'application/octet-stream' });
        });
        setTaskDescriptionDocumentFiles(placeholderFiles);
      } else {
        setTaskDescriptionDocumentFiles(taskDescriptionFiles);
      }
      setTaskDescriptionDocumentMetadata(taskDescriptionMetadata);
    } else {
      // Reset for add mode
      setPanelFormData({
        title: '',
        instruction: '',
        taskName: '',
        taskDescription: '',
        taskDurationDays: 1,
        hasTask: false,
        requireCommentOnFail: false,
        requireEvidenceOnFail: false,
        requireCommentOnPass: false,
        requireEvidenceOnPass: false,
        responseType:
          enableEnhancedResponseTypes && formData.hasConditionalSubQuestions
            ? ResponseType.TEXT
            : ResponseType.PASS_FAIL,
        showWhenParentIs: ShowOn.BOTH,
        requireComment: false,
        requireAttachment: false,
        options: [],
      });
      setInstructionDocumentFiles([]);
      setInstructionDocumentMetadata([]);
      setTaskDescriptionDocumentFiles([]);
      setTaskDescriptionDocumentMetadata([]);
    }
  }, [mode, editingIndex]);

  // Validation function
  const isFormValid = useCallback(() => {
    // Check mandatory fields
    if (!panelFormData.title.trim() || !panelFormData.instruction.trim()) {
      return false;
    }

    // In isFormValid function, add this check inside the hasTask condition
    if (panelFormData.hasTask) {
      if (
        !panelFormData.taskName.trim() ||
        !panelFormData.taskDescription.trim() ||
        panelFormData.taskDurationDays <= 0
      ) {
        return false;
      }
    }

    // ERT: Validate options for select types
    if (
      enableEnhancedResponseTypes &&
      (panelFormData.responseType === ResponseType.SINGLE_SELECT ||
        panelFormData.responseType === ResponseType.MULTI_SELECT)
    ) {
      const nonEmptyOptions = panelFormData.options.filter((opt) => opt.trim());
      if (nonEmptyOptions.length === 0) {
        return false;
      }
    }

    return true;
  }, [panelFormData, enableEnhancedResponseTypes]);

  // Handle saving sub-question
  const handleSave = useCallback(() => {
    // Validate form before saving
    if (!isFormValid()) {
      return;
    }

    setFileList((prev) => ({ ...prev, ...subQuestionFiles }));

    // Persist document files to parent state
    if (setDocumentFiles && setDocumentFilesMetadata) {
      const subQuestionIndex =
        editingIndex !== undefined ? editingIndex : formData.subQuestions?.length || 0;

      // Save instruction documents
      const instructionFieldKey = getSubQuestionFieldKey(subQuestionIndex, 'instruction');
      if (instructionDocumentFiles.length > 0 || instructionDocumentMetadata.length > 0) {
        setDocumentFiles((prev) => ({
          ...prev,
          [instructionFieldKey]: instructionDocumentFiles,
        }));
        setDocumentFilesMetadata((prev) => ({
          ...prev,
          [instructionFieldKey]: instructionDocumentMetadata,
        }));
      } else {
        // Clear if no documents
        setDocumentFiles((prev) => {
          const updated = { ...prev };
          delete updated[instructionFieldKey];
          return updated;
        });
        setDocumentFilesMetadata((prev) => {
          const updated = { ...prev };
          delete updated[instructionFieldKey];
          return updated;
        });
      }

      // Save taskDescription documents
      const taskDescriptionFieldKey = getSubQuestionFieldKey(
        subQuestionIndex,
        'taskDescription',
      );
      if (
        taskDescriptionDocumentFiles.length > 0 ||
        taskDescriptionDocumentMetadata.length > 0
      ) {
        setDocumentFiles((prev) => ({
          ...prev,
          [taskDescriptionFieldKey]: taskDescriptionDocumentFiles,
        }));
        setDocumentFilesMetadata((prev) => ({
          ...prev,
          [taskDescriptionFieldKey]: taskDescriptionDocumentMetadata,
        }));
      } else {
        // Clear if no documents
        setDocumentFiles((prev) => {
          const updated = { ...prev };
          delete updated[taskDescriptionFieldKey];
          return updated;
        });
        setDocumentFilesMetadata((prev) => {
          const updated = { ...prev };
          delete updated[taskDescriptionFieldKey];
          return updated;
        });
      }
    }

    const newSubQuestion: SubQuestion = {
      title: panelFormData.title,
      instruction: panelFormData.instruction,
      orderIndex:
        editingIndex !== undefined
          ? formData.subQuestions?.[editingIndex]?.orderIndex || 0
          : (formData.subQuestions?.length || 0) + 1,
      taskDetails: panelFormData.hasTask
        ? {
            taskName: panelFormData.taskName,
            taskDescription: panelFormData.taskDescription,
            taskDurationDays: panelFormData.taskDurationDays,
          }
        : undefined,
      // P/F requirement toggles: only for PASS_FAIL type
      ...((!enableEnhancedResponseTypes ||
        !isDataCollectionType(panelFormData.responseType)) && {
        requireCommentOnFail: panelFormData.requireCommentOnFail,
        requireEvidenceOnFail: panelFormData.requireEvidenceOnFail,
        requireCommentOnPass: panelFormData.requireCommentOnPass,
        requireEvidenceOnPass: panelFormData.requireEvidenceOnPass,
      }),
      ...(enableEnhancedResponseTypes && {
        responseType: panelFormData.responseType,
        ...(formData.hasConditionalSubQuestions && {
          showWhenParentIs: panelFormData.showWhenParentIs,
        }),
        // Unconditional requirement toggles: only for data-collection types
        ...(isDataCollectionType(panelFormData.responseType) && {
          requireComment: panelFormData.requireComment,
          requireAttachment: panelFormData.requireAttachment,
        }),
        options: isSelectType(panelFormData.responseType)
          ? panelFormData.responseType === ResponseType.YES_NO
            ? ['Yes', 'No']
            : panelFormData.options.filter((opt) => opt.trim())
          : undefined,
      }),
    };

    if (editingIndex !== undefined) {
      // Editing existing sub-question
      const updatedSubQuestions = [...(formData.subQuestions || [])];
      const merged = {
        ...updatedSubQuestions[editingIndex],
        ...newSubQuestion,
      };
      // Clean up stale toggle fields based on current responseType
      if (enableEnhancedResponseTypes) {
        if (isDataCollectionType(panelFormData.responseType)) {
          // Data-collection: remove P/F toggles
          delete merged.requireCommentOnFail;
          delete merged.requireEvidenceOnFail;
          delete merged.requireCommentOnPass;
          delete merged.requireEvidenceOnPass;
        } else {
          // Pass/Fail: remove unconditional toggles
          delete merged.requireComment;
          delete merged.requireAttachment;
        }
        // Remove options if not a select type
        if (!isSelectType(panelFormData.responseType)) {
          delete merged.options;
        }
      }
      updatedSubQuestions[editingIndex] = merged;
      setFormData((prev) => ({ ...prev, subQuestions: updatedSubQuestions }));
    } else {
      // Adding new sub-question
      const updatedSubQuestions = [...(formData.subQuestions || []), newSubQuestion];
      setFormData((prev) => ({ ...prev, subQuestions: updatedSubQuestions }));
    }

    // Close panel
    onClose();
  }, [
    panelFormData,
    editingIndex,
    formData.subQuestions,
    setFormData,
    onClose,
    isFormValid,
    subQuestionFiles,
    setFileList,
    instructionDocumentFiles,
    instructionDocumentMetadata,
    taskDescriptionDocumentFiles,
    taskDescriptionDocumentMetadata,
    setDocumentFiles,
    setDocumentFilesMetadata,
  ]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  // Handle adding task
  const handleAddTask = useCallback(() => {
    setPanelFormData((prev) => ({
      ...prev,
      hasTask: true,
      taskName: '',
      taskDescription: '',
      taskDurationDays: 1,
    }));
  }, []);

  // Handle removing task
  const handleRemoveTask = useCallback(() => {
    setPanelFormData((prev) => ({
      ...prev,
      hasTask: false,
      taskName: '',
      taskDescription: '',
      taskDurationDays: 0,
    }));
  }, []);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Scrollable content area */}
      <div style={{ flex: 1, overflow: 'auto', padding: '0 0 16px 0' }}>
        <SpaceBetween size="m">
          <FormField
            data-testid="sub-question-title-field"
            label={<RequiredFieldLabel label="Sub-question Title" />}
            stretch
          >
            <Input
              data-testid="sub-question-title-input"
              value={panelFormData.title}
              onChange={({ detail }) =>
                setPanelFormData((prev) => ({ ...prev, title: detail.value }))
              }
              placeholder="Enter sub-question title"
            />
          </FormField>

          <FormField
            data-testid="sub-question-instruction-field"
            label={<RequiredFieldLabel label="Sub-question Instruction" />}
            stretch
            description={MARKDOWN_TEXT_WARNING}
          >
            <TextEditor
              data-testid="sub-question-instruction-editor"
              placeholder="Enter sub-question instruction..."
              value={panelFormData.instruction}
              onChange={(value) =>
                setPanelFormData((prev) => ({ ...prev, instruction: value }))
              }
              onChangeMedia={(data) =>
                setSubQuestionFiles((prev) => ({ ...prev, subquestionInstruction: data }))
              }
              showFileUpload={!!setDocumentFiles}
              initialFiles={instructionDocumentFiles}
              initialFileMetadata={memoizedInstructionMetadata}
              onFilesChange={(data) => {
                setInstructionDocumentFiles(data.files);
                setInstructionDocumentMetadata(data.metadata);
              }}
            />
          </FormField>

          {/* ERT: Response Type and Show On dropdowns */}
          {enableEnhancedResponseTypes && (
            <ColumnLayout data-testid="ert-response-type-row" columns={2}>
              <FormField
                data-testid="sq-response-type-field"
                label={<RequiredFieldLabel label="Response type" />}
                description="Only Pass/Fail is scored. Others capture structured data."
              >
                <Select
                  data-testid="sq-response-type-select"
                  selectedOption={
                    responseTypeOptions.find(
                      (opt) => opt.value === panelFormData.responseType,
                    ) || responseTypeOptions[0]
                  }
                  options={responseTypeOptions}
                  onChange={({ detail }) => {
                    const newType = detail.selectedOption.value as ResponseType;
                    const switchingToDataCollection = isDataCollectionType(newType);
                    const switchingToPF = !switchingToDataCollection;
                    setPanelFormData((prev) => ({
                      ...prev,
                      responseType: newType,
                      // Reset options when switching away from select types
                      options: isSelectType(newType) ? prev.options : [],
                      // Reset unconditional toggles when switching to P/F
                      ...(switchingToPF && {
                        requireComment: false,
                        requireAttachment: false,
                      }),
                      // Reset P/F toggles when switching to data-collection
                      ...(switchingToDataCollection && {
                        requireCommentOnFail: false,
                        requireEvidenceOnFail: false,
                        requireCommentOnPass: false,
                        requireEvidenceOnPass: false,
                      }),
                    }));
                  }}
                />
              </FormField>
              <FormField
                data-testid="sq-show-on-field"
                label={
                  formData.hasConditionalSubQuestions ? (
                    <RequiredFieldLabel label="Show when parent is" />
                  ) : (
                    'Show when parent is'
                  )
                }
                description={
                  formData.hasConditionalSubQuestions
                    ? 'Choose when this sub-question appears based on the parent answer.'
                    : 'Answer-based display is off, so this sub-question always appears.'
                }
              >
                <Select
                  data-testid="sq-show-on-select"
                  selectedOption={
                    showOnOptions.find(
                      (opt) => opt.value === panelFormData.showWhenParentIs,
                    ) || showOnOptions[2]
                  }
                  options={showOnOptions}
                  disabled={!formData.hasConditionalSubQuestions}
                  onChange={({ detail }) =>
                    setPanelFormData((prev) => ({
                      ...prev,
                      showWhenParentIs: detail.selectedOption.value as ShowOn,
                    }))
                  }
                />
              </FormField>
            </ColumnLayout>
          )}

          {/* ERT: Options editor for Single Select / Multi Select */}
          {enableEnhancedResponseTypes &&
            (panelFormData.responseType === ResponseType.SINGLE_SELECT ||
              panelFormData.responseType === ResponseType.MULTI_SELECT) && (
              <FormField
                data-testid="sq-configured-values-field"
                label={<RequiredFieldLabel label="Configured values" />}
                description={
                  panelFormData.responseType === ResponseType.MULTI_SELECT
                    ? 'Auditor picks one or more of these.'
                    : 'Auditor picks exactly one of these.'
                }
                errorText={
                  panelFormData.options.filter((opt) => opt.trim()).length === 0
                    ? 'At least one option is required.'
                    : undefined
                }
              >
                <SpaceBetween size="xs">
                  {panelFormData.options.map((option, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <Input
                          data-testid={`sq-option-input-${index}`}
                          value={option}
                          placeholder="Enter option value"
                          onChange={({ detail }) =>
                            setPanelFormData((prev) => {
                              const updatedOptions = [...prev.options];
                              updatedOptions[index] = detail.value;
                              return { ...prev, options: updatedOptions };
                            })
                          }
                        />
                      </div>
                      <Button
                        data-testid={`sq-option-remove-${index}`}
                        iconName="close"
                        variant="icon"
                        onClick={() =>
                          setPanelFormData((prev) => ({
                            ...prev,
                            options: prev.options.filter((_, i) => i !== index),
                          }))
                        }
                      />
                    </div>
                  ))}
                  <Button
                    data-testid="sq-add-option-button"
                    iconName="add-plus"
                    variant="link"
                    onClick={() =>
                      setPanelFormData((prev) => ({
                        ...prev,
                        options: [...prev.options, ''],
                      }))
                    }
                  >
                    Add value
                  </Button>
                </SpaceBetween>
              </FormField>
            )}

          {/* Requirement toggles: show P/F toggles for Pass/Fail type, unconditional toggles for data-collection */}
          {enableEnhancedResponseTypes &&
          isDataCollectionType(panelFormData.responseType) ? (
            <ColumnLayout
              data-testid="sub-question-data-collection-requirements"
              columns={2}
            >
              <FormField data-testid="sq-require-comment-field" label="Comment mandatory">
                <Toggle
                  data-testid="sq-require-comment-toggle"
                  checked={panelFormData.requireComment}
                  onChange={({ detail }) =>
                    setPanelFormData((prev) => ({
                      ...prev,
                      requireComment: detail.checked,
                    }))
                  }
                />
              </FormField>
              <FormField
                data-testid="sq-require-attachment-field"
                label="Attachment mandatory"
              >
                <Toggle
                  data-testid="sq-require-attachment-toggle"
                  checked={panelFormData.requireAttachment}
                  onChange={({ detail }) =>
                    setPanelFormData((prev) => ({
                      ...prev,
                      requireAttachment: detail.checked,
                    }))
                  }
                />
              </FormField>
            </ColumnLayout>
          ) : (
            <>
              <ColumnLayout data-testid="sub-question-fail-requirements" columns={2}>
                <FormField
                  data-testid="sq-require-comment-on-fail-field"
                  label="Comment Required on Fail"
                >
                  <Toggle
                    data-testid="sq-require-comment-on-fail-toggle"
                    checked={panelFormData.requireCommentOnFail}
                    onChange={({ detail }) =>
                      setPanelFormData((prev) => ({
                        ...prev,
                        requireCommentOnFail: detail.checked,
                      }))
                    }
                  />
                </FormField>
                <FormField
                  data-testid="sq-require-evidence-on-fail-field"
                  label="Attachment Required on Fail"
                >
                  <Toggle
                    data-testid="sq-require-evidence-on-fail-toggle"
                    checked={panelFormData.requireEvidenceOnFail}
                    onChange={({ detail }) =>
                      setPanelFormData((prev) => ({
                        ...prev,
                        requireEvidenceOnFail: detail.checked,
                      }))
                    }
                  />
                </FormField>
              </ColumnLayout>

              <ColumnLayout data-testid="sub-question-pass-requirements" columns={2}>
                <FormField
                  data-testid="sq-require-comment-on-pass-field"
                  label="Comment Required on Pass"
                >
                  <Toggle
                    data-testid="sq-require-comment-on-pass-toggle"
                    checked={panelFormData.requireCommentOnPass}
                    onChange={({ detail }) =>
                      setPanelFormData((prev) => ({
                        ...prev,
                        requireCommentOnPass: detail.checked,
                      }))
                    }
                  />
                </FormField>
                <FormField
                  data-testid="sq-require-evidence-on-pass-field"
                  label="Attachment Required on Pass"
                >
                  <Toggle
                    data-testid="sq-require-evidence-on-pass-toggle"
                    checked={panelFormData.requireEvidenceOnPass}
                    onChange={({ detail }) =>
                      setPanelFormData((prev) => ({
                        ...prev,
                        requireEvidenceOnPass: detail.checked,
                      }))
                    }
                  />
                </FormField>
              </ColumnLayout>
            </>
          )}

          {panelFormData.hasTask ? (
            <SpaceBetween size="m">
              <Header
                data-testid="task-details-header"
                variant="h3"
                actions={
                  <Button
                    data-testid="remove-task-button"
                    iconName="remove"
                    variant="link"
                    onClick={handleRemoveTask}
                  >
                    Remove Task
                  </Button>
                }
              >
                Task Details
              </Header>

              <FormField
                data-testid="task-name-field"
                label={<RequiredFieldLabel label="Task Name" />}
                stretch
              >
                <Input
                  data-testid="task-name-input"
                  value={panelFormData.taskName}
                  onChange={({ detail }) =>
                    setPanelFormData((prev) => ({ ...prev, taskName: detail.value }))
                  }
                  placeholder="Enter task name"
                />
              </FormField>

              <FormField
                data-testid="task-description-field"
                label={<RequiredFieldLabel label="Task Description" />}
                stretch
                description={MARKDOWN_TEXT_WARNING}
              >
                <TextEditor
                  data-testid="task-description-editor"
                  placeholder="Enter task description..."
                  value={panelFormData.taskDescription}
                  onChange={(value) =>
                    setPanelFormData((prev) => ({ ...prev, taskDescription: value }))
                  }
                  onChangeMedia={(data) =>
                    setSubQuestionFiles((prev) => ({ ...prev, subTaskDescription: data }))
                  }
                  showFileUpload={!!setDocumentFiles}
                  initialFiles={taskDescriptionDocumentFiles}
                  initialFileMetadata={memoizedTaskDescriptionMetadata}
                  onFilesChange={(data) => {
                    setTaskDescriptionDocumentFiles(data.files);
                    setTaskDescriptionDocumentMetadata(data.metadata);
                  }}
                />
              </FormField>

              <FormField
                data-testid="task-duration-field"
                label="Task Duration (Days)"
                stretch
              >
                <Input
                  data-testid="task-duration-input"
                  type="number"
                  value={panelFormData.taskDurationDays.toString()}
                  onChange={({ detail }) =>
                    setPanelFormData((prev) => ({
                      ...prev,
                      taskDurationDays: parseInt(detail.value) || 0,
                    }))
                  }
                  placeholder="0"
                />
              </FormField>
            </SpaceBetween>
          ) : (
            <Box data-testid="add-task-container" textAlign="right">
              <Button
                data-testid="add-task-button"
                iconName="add-plus"
                onClick={handleAddTask}
              >
                Add Task
              </Button>
            </Box>
          )}
        </SpaceBetween>
      </div>

      {/* Sticky footer with buttons */}
      <div
        style={{
          borderTop: '1px solid #e9ebed',
          padding: '16px 0 0 0',
          backgroundColor: 'white',
        }}
      >
        <Box data-testid="action-buttons-container" float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button data-testid="cancel-button" variant="link" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              data-testid="save-button"
              variant="primary"
              onClick={handleSave}
              disabled={!isFormValid()}
            >
              Save
            </Button>
          </SpaceBetween>
        </Box>
      </div>
    </div>
  );
};

export default SubQuestionSidePanel;
